(function ($, window, document) {
    'use strict';

    /**
     * Configuration object used across the application.
     *
     * @typedef {Object} CFG
     *
     * @property {string} baseUrl - The base URL used for API calls. Defaults to '/user' or takes the value from 'window.WayGenAI.apiBase'.
     * @property {string} redirect - The URL to redirect users to in case of certain operations (e.g., after login). Defaults to '/login/' or takes the value from 'window.WayGenAI.redirect'.
     * @property {Object} i18n - Internationalization strings providing user-facing messages. Merges defaults with any custom strings in 'window.WayGenAI.i18n'.
     * @property {string} i18n.emailSent - Message displayed when a password reset email is successfully sent.
     * @property {string} i18n.invalidEmail - Message displayed when an invalid email is provided.
     * @property {string} i18n.setSuccess - Message displayed when the password is successfully updated.
     * @property {string} i18n.invalidToken - Message displayed when the token provided is invalid or expired.
     * @property {string} i18n.tokenExpired - Message displayed when the reset token has expired.
     * @property {string} i18n.pwWeak - Message displayed when the password does not meet strength requirements.
     * @property {string} i18n.pwMismatch - Message displayed when the passwords entered do not match.
     * @property {string} i18n.fallback - Generic fallback message displayed in case of an unknown error.
     * @property {string} i18n.strong - Label indicating a strong password.
     * @property {string} i18n.weak - Label indicating a weak password.
     */
    const CFG = {
        baseUrl: window.WayGenAI?.apiBase || '/user',
        redirect: window.WayGenAI?.redirect || '/user/log-in/',
        i18n: Object.assign({
            emailSent: 'If this email exists, a password reset link has been sent.',
            invalidEmail: 'Please enter a valid email address.',
            setSuccess: 'Password updated. Redirecting to login…',
            invalidToken: 'Invalid or expired token.',
            tokenExpired: 'Token expired, please request a new password reset.',
            pwWeak: 'Password does not meet the strength requirements.',
            pwMismatch: 'Passwords do not match.',
            fallback: 'Something went wrong. Please try again.',
            strong: 'strong',
            weak: 'weak'
        }, window.WayGenAI?.i18n || {})
    };

    /**
     * Redirects to a safe URL based on the provided input. This method ensures that the URL is on the same origin,
     * uses a valid HTTP/HTTPS protocol, and optionally retains the query parameters.
     *
     * @param {string} url - The target URL to redirect to. This can be either a relative or an absolute URL.
     * @param {Object} [options] - Additional options to customize the redirection behavior.
     * @param {string} [options.fallback='/user/'] - The fallback URL to use if the provided URL is invalid or unsafe.
     * @param {boolean} [options.keepQuery=false] - A flag indicating whether to retain query parameters in the URL.
     * @return {string} The normalized and safe URL to which the redirection can occur.
     */
    function safeRedirect(url, {fallback = '/user/', keepQuery = false} = {}) {
        try {
            const base = window.location.origin;

            // treat url as relative against current origin
            const u = new URL(typeof url === 'string' ? url : '', base);

            // block weird schemes and scheme-relative
            if (u.protocol !== 'https:' && u.protocol !== 'http:') return fallback;

            // only same-origin allowed
            if (u.origin !== base) return fallback;

            // only paths under this site; normalize to path (optionally keep query)
            const path = u.pathname.startsWith('/') ? u.pathname : '/' + u.pathname;
            const normalized = path.endsWith('/') ? path : path + '/';
            return normalized + (keepQuery ? u.search : '');
        } catch {
            return fallback;
        }
    }

    /**
     * Clears the cached user information after the API invalidates the session cookie.
     *
     * @return {void} This method does not return a value.
     */
    function clearCachedUser() {
        try {
            localStorage.removeItem('wg_user');
        } catch (_) {
        }
    }

    /**
     * Retrieves the value of a specified query parameter from the current page's URL.
     *
     * @param {string} name - The name of the query parameter to retrieve.
     * @return {string|null} The decoded value of the query parameter if found, or null if not found.
     */
    function q(name) {
        const m = new RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
        return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
    }

    /**
     * Validates if the given input is a properly formatted email address.
     *
     * @param {string} v The input string to validate as an email address.
     * @return {boolean} Returns true if the input is a valid email address, otherwise false.
     */
    function isEmail(v) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    /**
     * Determines if a password is strong based on specified criteria:
     * - At least 8 characters long.
     * - Contains at least one lowercase letter.
     * - Contains at least one uppercase letter.
     * - Contains at least one numeric digit.
     * - Contains at least one special character.
     * - Consists of valid characters only.
     *
     * @param {string} pw - The password to validate.
     * @return {boolean} Returns true if the password meets all strength requirements, otherwise false.
     */
    function isStrongPassword(pw) {
        if (!pw || pw.length < 8) return false;
        if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};'\:"|,<.>\/\?`~]+$/.test(pw)) return false;
        if (!/[a-z]/.test(pw)) return false;
        if (!/[A-Z]/.test(pw)) return false;
        if (!/[0-9]/.test(pw)) return false;
        if (!/[^a-zA-Z0-9]/.test(pw)) return false;
        return true;
    }

    /**
     * Displays a global message by updating the text of the specified element and making it visible.
     *
     * @param {jQuery} $el - The jQuery object representing the target DOM element.
     * @param {string} msg - The message to be displayed in the target element.
     * @return {void} This function does not return a value.
     */
    function showGlobal($el, msg) {
        $el.text(msg).show();
    }

    /**
     * Clears the content of a global element by hiding it and removing its text content.
     *
     * @param {jQuery} $el - The jQuery object representing the DOM element to be cleared.
     * @return {void} No return value.
     */
    function clearGlobal($el) {
        $el.hide().text('');
    }

    /**
     * Displays an error message associated with a specific field and applies an invalid class to the field.
     *
     * @param {string} field - The identifier of the field for which the error needs to be displayed.
     * @param {string} msg - The error message to be displayed for the specified field.
     * @return {void} This method does not return a value.
     */
    function showFieldError(field, msg) {
        const $err = $(`[data-error-for="${field}"]`);
        if ($err.length) $err.text(msg);
        $(`#${field}`).addClass('is-invalid');
    }

    /**
     * Clears all field error messages and removes error styling within the given scope.
     *
     * @param {HTMLElement|string} scope - The scope (DOM element or selector) within which field errors should be cleared.
     * @return {void} This method does not return a value.
     */
    function clearFieldErrors(scope) {
        $(scope).find('.field-error').text('');
        $(scope).find('.input').removeClass('is-invalid');
    }

    /**
     * Toggles the loading state of a button by enabling or disabling it
     * and showing or hiding a spinner element inside the button.
     *
     * @param {jQuery} $btn - The jQuery object representing the button to modify.
     * @param {boolean} on - A boolean indicating the loading state.
     *                        If true, disables the button and shows the spinner.
     *                        If false, enables the button and hides the spinner.
     * @return {void}
     */
    function setLoading($btn, on) {
        $btn.prop('disabled', on);
        $btn.find('.spinner').toggle(on);
    }

    // toggle password visibility with icon switch
    $(document).on('click', '.pw-toggle', function () {
        const $btn = $(this);
        const $icon = $btn.find('i');
        const $input = $('#rp-password');
        const isHidden = $input.attr('type') === 'password';
        $input.attr('type', isHidden ? 'text' : 'password');
        if (isHidden) $icon.removeClass('fa-eye-slash').addClass('fa-eye');
        else $icon.removeClass('fa-eye').addClass('fa-eye-slash');
    });

    // strength meter text + bar
    $('#rp-password').on('input', function () {
        const pw = $(this).val();
        const strong = isStrongPassword(pw);
        const $m = $('#rp-meter');
        $m.removeClass('ok weak');
        if (!pw) {
            $m.text('');
            return;
        }
        if (strong) $m.addClass('ok').text(CFG.i18n.strong);
        else $m.addClass('weak').text(CFG.i18n.weak);
    });

    // mode switch by token
    (function initMode() {
        const token = q('token') || $('#rp-token').val();
        if (token) {
            $('#wg-reset-request-form').hide();
            $('#wg-reset-set-form').show();
            $('#rp-token').val(token);
        } else {
            $('#wg-reset-request-form').show();
            $('#wg-reset-set-form').hide();
        }
    })();

    // submit: request reset link
    $(document).on('submit', '#wg-reset-request-form', function (e) {
        e.preventDefault();
        clearFieldErrors('#wg-reset-request-form');
        clearGlobal($('#rp-request-global'));
        const $btn = $('#rp-request-submit');
        const email = $('#rp-email').val().trim();

        if (!isEmail(email)) {
            showFieldError('rp-email', CFG.i18n.invalidEmail);
            return;
        }

        setLoading($btn, true);

        $.ajax({
            url: CFG.baseUrl + '/request-password-reset?email=' + encodeURIComponent(email),
            method: 'POST',
            dataType: 'json'
        })
            .done(function () {
                showGlobal($('#rp-request-success'), CFG.i18n.emailSent);
                $('#rp-email').val('').trigger('input').trigger('change');
            })
            .fail(function (jqXHR) {
                const resp = jqXHR.responseJSON;
                showGlobal($('#rp-request-global'), resp?.detail || CFG.i18n.fallback);
            })
            .always(function () {
                setLoading($btn, false);
            });
    });

    // submit: set new password with token
    $(document).on('submit', '#wg-reset-set-form', function (e) {
        e.preventDefault();
        clearFieldErrors('#wg-reset-set-form');
        clearGlobal($('#rp-set-global'));
        const $btn = $('#rp-set-submit');

        const token = $('#rp-token').val().trim();
        const pw1 = $('#rp-password').val();
        const pw2 = $('#rp-password2').val();

        if (!isStrongPassword(pw1)) {
            showFieldError('rp-password', CFG.i18n.pwWeak);
            return;
        }
        if (pw1 !== pw2) {
            showFieldError('rp-password2', CFG.i18n.pwMismatch);
            return;
        }

        setLoading($btn, true);

        $.ajax({
            url: CFG.baseUrl + '/reset-password',
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify({token: token, new_password: pw1})
        })
            .done(function () {
                clearCachedUser();
                showGlobal($('#rp-set-success'), CFG.i18n.setSuccess);
                setTimeout(function () {
                    window.location.assign(safeRedirect(CFG.redirect, { fallback: '/user/log-in/' }));
                }, 900);
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                const resp = jqXHR.responseJSON;

                if (status === 400) {
                    const d = (resp?.detail || '').toLowerCase();
                    const isExpired = d.indexOf('expired') !== -1;
                    const msg = isExpired ? CFG.i18n.tokenExpired : CFG.i18n.invalidToken;

                    $('#wg-reset-set-form').hide();
                    $('#wg-reset-request-form').show();

                    $('#rp-token').val('');
                    try {
                        window.history.replaceState({}, document.title, window.location.pathname);
                    } catch (_) {
                    }

                    $('#rp-request-success').hide().text('');
                    showGlobal($('#rp-request-global'), msg);
                    $('#rp-email').trigger('focus')[0]?.scrollIntoView({behavior: 'smooth', block: 'center'});
                    return;
                }

                if (status === 422 && Array.isArray(resp?.detail)) {
                    resp.detail.forEach(function (err) {
                        const loc = (err.loc || []).slice(-1)[0];
                        const msg = err.msg || CFG.i18n.pwWeak;
                        if (loc === 'new_password') showFieldError('rp-password', msg);
                        else showGlobal($('#rp-set-global'), msg);
                    });
                    return;
                }

                showGlobal($('#rp-set-global'), resp?.detail || CFG.i18n.fallback);
            })
            .always(function () {
                setLoading($btn, false);
            });
    });

})(jQuery, window, document);
